// Word-level marking inside a *changed sentence pair*.
//
// The server (`turnTextDiff.ts`) does the expensive part — sentence-level
// LCS across whole sections — because it has both snapshots in hand. It
// deliberately stops there: shipping word-level spans over the wire would
// multiply the persisted payload for information the browser can derive in
// microseconds. `diff-match-patch` is already bundled on the client, so the
// fine-grained pass happens here.

import DiffMatchPatch from 'diff-match-patch';

export interface WordDiffToken {
  op: 'equal' | 'insert' | 'delete';
  text: string;
}

const dmp = new DiffMatchPatch();

/**
 * Marks the words that actually differ between a removed and an added
 * sentence. Semantic cleanup is applied so the result reads as "this clause
 * changed" rather than a shower of single-character edits.
 */
export function wordDiff(before: string, after: string): WordDiffToken[] {
  const diffs = dmp.diff_main(before, after);
  dmp.diff_cleanupSemantic(diffs);
  return diffs.map(([op, text]) => ({
    op: op === 1 ? 'insert' : op === -1 ? 'delete' : 'equal',
    text,
  }));
}

/**
 * True when two sentences are close enough that showing them as a
 * rewrite (one line, inline marks) is clearer than as a separate removal
 * followed by a separate addition.
 */
export function isRewriteOf(before: string, after: string): boolean {
  if (!before || !after) return false;
  const diffs = dmp.diff_main(before, after);
  dmp.diff_cleanupSemantic(diffs);
  let common = 0;
  for (const [op, text] of diffs) if (op === 0) common += text.length;
  const longest = Math.max(before.length, after.length);
  return longest > 0 && common / longest >= 0.4;
}
